import { ApplicationSummaryDto, AssignedConfigsDto } from '@inosio/payments-sdk';
import { Box, Card, Grid, Link, Modal, Table, TableBody, TableCell, TableContainer, TableRow, Tooltip, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import CancelIcon from '../../../icons/close.svg';
import { getScreeningOrderDetails, selectApplicationsState } from '../../../resui/applications/sagas/pm/applicationsSlice';
import SpinnerModal from '../../../shared/Modal/SpinnerModal';
import DownloadIcon from '../../../icons/DownloadIcon.svg';
import { LoadingButton } from '@mui/lab';
import { selectPropertyGroupDetailsState } from '../../../shared/sagas/propertyGroups/propertyGroupsSlice';

export interface ScreeningOrderDownloadModalProps {
  open: boolean;
  onClose: () => void;
  application: ApplicationSummaryDto;
}

export default function ScreeningOrderDownloadModal(props: ScreeningOrderDownloadModalProps) {
  const dispatch = useDispatch();

  const style = {
    position: 'absolute' as 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    padding: '24px',
    height: '523px',
    pointerEvents: 'auto',
    width: '472px',
    borderRadius: '24px',
  };

  const [availableScreeningDownloads, setAvailableScreeningDownloads] = useState<Map<string, { displayName: string; url: string }>>(new Map());
  const [isSpinnerModalOpen, setIsSpinnerModalOpen] = React.useState(false);
  const [spinnerStatus] = React.useState<string | null>(null);
  const { allPropertyGroupsAssignedConfigs } = useSelector(selectPropertyGroupDetailsState);
  const propGroupConfigs = allPropertyGroupsAssignedConfigs?.find((pgc) => pgc.Uuid === props.application.OrganizationUuid) || ({} as AssignedConfigsDto);
  const { getScreeningOrderDetailsResponse, isGettingScreeningOrderDetails } = useSelector(selectApplicationsState);

  useEffect(() => {
    setIsSpinnerModalOpen(isGettingScreeningOrderDetails);
  }, [isGettingScreeningOrderDetails]);

  useEffect(() => {
    const coapplicants = props.application.Applicants.filter((a) => a.Uuid !== props.application.PrimaryApplicantUuid);
    props.application?.ScreeningOrders?.forEach((so) => {
      const correspondingApplicant = coapplicants?.find((ca) => ca.Uuid === so.ApplicantUuid);
      if (correspondingApplicant) {
        const displayName = `${correspondingApplicant.FirstName} ${correspondingApplicant.LastName}`;
        availableScreeningDownloads.set(so.ScreeningOrderUuid, { displayName: displayName, url: '' });
        setAvailableScreeningDownloads(availableScreeningDownloads);
        return dispatch({ type: getScreeningOrderDetails.type, payload: { screeningOrderUuid: so.ScreeningOrderUuid } });
      }

      const primaryApplicant = props.application.Applicants.find((a) => a.Uuid === props.application.PrimaryApplicantUuid);
      if (primaryApplicant) {
        const displayName = `${primaryApplicant.FirstName} ${primaryApplicant.LastName}`;
        availableScreeningDownloads.set(so.ScreeningOrderUuid, { displayName: displayName, url: '' });
        setAvailableScreeningDownloads(availableScreeningDownloads);
        return dispatch({ type: getScreeningOrderDetails.type, payload: { screeningOrderUuid: so.ScreeningOrderUuid } });
      }
    });
  }, [dispatch]);

  React.useEffect(() => {
    if (getScreeningOrderDetailsResponse) {
      const newMap = new Map(availableScreeningDownloads);
      const existingMapEntry = newMap.get(getScreeningOrderDetailsResponse.ScreeningOrderUuid);
      if (existingMapEntry) {
        existingMapEntry.url = getScreeningOrderDetailsResponse.PdfUrl!;
        setAvailableScreeningDownloads(newMap);
      }
    }
  }, [getScreeningOrderDetailsResponse]);

  const handleCloseSpinnerModal = React.useCallback(() => {
    setIsSpinnerModalOpen(false);
  }, [dispatch]);

  return (
    <div>
      <Modal
        open={props.open}
        aria-labelledby="simple-modal-title"
        aria-describedby="simple-modal-description"
        BackdropProps={{
          invisible: false,
        }}
        disableEscapeKeyDown={false}
        disableAutoFocus={true}
      >
        <Card id="downloadScreeningFiles" sx={style}>
          {propGroupConfigs.ScreeningEnabled && (
            <Grid container width="100%" display="flex" justifyContent="center">
              <Grid item xs={12} display="flex" justifyContent="center">
                <Typography
                  sx={{
                    fontFamily: 'Work Sans !important',
                    fontStyle: 'normal',
                    fontWeight: '600',
                    fontSize: '18px !important',
                    lineHeight: '21px !important',
                    color: '#14142B',
                  }}
                >
                  Screening Reports
                </Typography>
              </Grid>
              <Grid item xs={12} display="flex" justifyContent="center" mt={2}>
                <TableContainer style={{ flex: 1 }}>
                  <Table size="small" stickyHeader>
                    <TableBody style={{ left: '28px' }}>
                      {Array.from(availableScreeningDownloads.values()).map((val, i) => {
                        return (
                          <TableRow>
                            <TableCell id="content">{val.displayName}</TableCell>
                            <TableCell id="content">
                              <Tooltip title="Download Screening Files" placement="top" onClick={(e) => {}}>
                                <Link key={'l' + i} href={val.url}>
                                  <img width={13} height={14} src={DownloadIcon} alt="export" style={{ cursor: 'pointer' }} />
                                </Link>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
              {/* Save button */}
              <Box sx={{ position: 'absolute', bottom: 15, width: '94%' }}>
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <LoadingButton
                    variant="contained"
                    disabled={false}
                    style={{
                      textTransform: 'none',
                      fontFamily: "'Open Sans', sans-serif",
                      fontStyle: 'normal',
                      fontWeight: 700,
                      backgroundColor: '#39A2DB',
                      borderRadius: '24px',
                      width: '150px',
                    }}
                    onClick={props.onClose}
                  >
                    Close
                  </LoadingButton>
                </Box>
              </Box>
              {/* Close Icon */}
              <Grid item sx={{ position: 'absolute', top: 18, right: 20 }}>
                <img src={CancelIcon} alt="close" style={{ cursor: 'pointer' }} onClick={props.onClose} />
              </Grid>
            </Grid>
          )}
        </Card>
      </Modal>
      {isSpinnerModalOpen ? (
        <SpinnerModal
          width="472px"
          height="523px"
          openSpinnerModal={isSpinnerModalOpen}
          handleCloseSpinnerModal={handleCloseSpinnerModal}
          spinnerStatus={spinnerStatus}
          closeCancel={() => setIsSpinnerModalOpen(false)}
        />
      ) : (
        <></>
      )}{' '}
    </div>
  );
}
